import type { ITenantDestination } from '@/entities/tenant-destination'
import type { PoiType } from '@/shared/api/types/poi-types'
import { useMemo } from 'react'
import { usePoiCategoriesInfiniteQuery, usePoiTypesQuery } from '@/entities/poi/api/poi-queries'
import {
  useTenantDestinationByIdQuery,
  useTenantDestinationsQuery,
} from '@/entities/tenant-destination'
import {
  buildCategoryOptions,
  buildTypeOptions,
  resolveDestinationCenter,
} from '@/features/poi/poi-form-model'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'
import { POI_FORM_CATEGORY_PAGE_SIZE } from '@/shared/constants/poi-form'

interface UsePoiFormOptionsParams {
  tenantId: string
  tenantDestinationId: string
  formLanguageCode: string
  tenantDefaultLanguage: string
  currentType: PoiType | ''
}

/** Destination, category and type choices of the POI form, plus the destination the map should frame. */
export function usePoiFormOptions({
  tenantId,
  tenantDestinationId,
  formLanguageCode,
  tenantDefaultLanguage,
  currentType,
}: UsePoiFormOptionsParams) {
  const { data: destinationsResponse, isLoading: isDestinationsLoading } =
    useTenantDestinationsQuery({
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      isActive: true,
      ...(formLanguageCode ? { languageCode: formLanguageCode } : {}),
    })
  const destinations: ITenantDestination[] = destinationsResponse?.data?.data ?? []

  // * VI: Điểm đến đang chọn có thể không nằm trong trang danh sách đã tải; query riêng theo id để vẫn hiển thị đúng và lấy được ranh giới/tâm cho bản đồ. | EN: The selected destination may not be in the loaded list; fetch it by id so it still renders and supplies the boundary/centre for the map.
  const { data: selectedDestinationResponse } = useTenantDestinationByIdQuery(
    tenantDestinationId,
    formLanguageCode || undefined,
    { enabled: !!formLanguageCode },
  )
  const selectedDestination =
    selectedDestinationResponse?.data ??
    destinations.find((item) => item.id === tenantDestinationId)
  const destinationCenter = useMemo(
    () => resolveDestinationCenter(selectedDestination),
    [selectedDestination],
  )

  const categoriesQuery = usePoiCategoriesInfiniteQuery(tenantId, {
    ...(tenantDefaultLanguage ? { languageCode: tenantDefaultLanguage } : {}),
    pageSize: POI_FORM_CATEGORY_PAGE_SIZE,
  })
  const categoryOptions = useMemo(
    () =>
      buildCategoryOptions(
        categoriesQuery.data?.pages.flatMap((page) => page.data) ?? [],
        [formLanguageCode, tenantDefaultLanguage].filter(Boolean),
      ),
    [categoriesQuery.data?.pages, formLanguageCode, tenantDefaultLanguage],
  )

  const typesQuery = usePoiTypesQuery(formLanguageCode || undefined)
  const typeOptions = useMemo(
    () => buildTypeOptions(typesQuery.data?.data ?? [], currentType),
    [currentType, typesQuery.data?.data],
  )

  return {
    destinations,
    isDestinationsLoading,
    selectedDestination,
    destinationCenter,
    categoryOptions,
    typeOptions,
    categoriesQuery,
  }
}
